import { ChainData, ChainType, Token } from "@0xsquid/squid-types";
import {
  Asset,
  BASE_FEE,
  Operation,
  rpc,
  Transaction,
  TransactionBuilder,
} from "@stellar/stellar-sdk";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import BigNumber from "bignumber.js";
import { getClient } from "../../core/client";
import { getPrefixKey, keys, QueryKeys } from "../../core/queries/queries-keys";
import { StellarTrustLine } from "../../core/types/stellar";
import { formatBNToReadable } from "../../services";
import { StellarApiClient } from "../../services/external/stellarApiClient";
import {
  getStellarHorizonApiUrl,
  getStellarNetwork,
  getStellarTrustLineAsset,
  isStellarAddressValid,
  isStellarIssuedToken,
} from "../../services/internal/stellarService";
import { useSigner } from "../wallet/useSigner";

/**
 * Maximum asset amount on Stellar
 * @see https://developers.stellar.org/docs/learn/fundamentals/stellar-data-structures/assets#amount-precision
 */
const MAX_ASSET_AMOUNT = "922337203685.4775807";

const DEFAULT_REFETCH_INTERVAL = 20_000;

interface Params {
  address: string | undefined;
  token: Token | undefined;
  chain: ChainData | undefined;
  amount: bigint | undefined;
}

export function useStellarTrustLine({ address, chain, token, amount }: Params) {
  const { stellarSigner } = useSigner({ chain });
  const queryClient = useQueryClient();

  /**
   * Retrieves the destination account's trust line data for the given token
   */
  const trustLineQuery = useQuery<StellarTrustLine | null>({
    queryKey: keys().stellarTrustLine(token?.address, chain?.chainId, address),
    queryFn: async () => {
      if (
        chain?.chainType !== ChainType.STELLAR ||
        token?.type !== ChainType.STELLAR
      ) {
        return null;
      }

      if (!address || !isStellarAddressValid(address)) {
        return null;
      }

      // Only issued tokens require trust lines
      // Other token types like contract tokens don't need trust lines
      if (!isStellarIssuedToken(token)) {
        return null;
      }

      const asset = getStellarTrustLineAsset(token);

      if (!asset) {
        throw new Error("Invalid asset");
      }

      const horizonApiUrl = getStellarHorizonApiUrl(chain);

      if (!horizonApiUrl) {
        throw new Error("Invalid Horizon API URL");
      }

      const stellarApiClient = new StellarApiClient(horizonApiUrl);

      return stellarApiClient.getTrustLine(address, asset);
    },
    enabled:
      !!address &&
      chain?.chainType === ChainType.STELLAR &&
      token?.type === ChainType.STELLAR,
    refetchInterval: DEFAULT_REFETCH_INTERVAL,
  });

  /**
   * Creates a trust line where the destination account authorizes to receive the given token
   */
  const createTrustLine = useMutation<boolean>({
    mutationFn: async () => {
      try {
        if (!stellarSigner) {
          throw new Error("Stellar signer not found");
        }

        if (!address) {
          throw new Error("Destination address is required");
        }

        if (
          chain?.chainType !== ChainType.STELLAR ||
          token?.type !== ChainType.STELLAR
        ) {
          throw new Error("Chain and token to approve must be a Stellar token");
        }

        if (!isStellarIssuedToken(token)) {
          throw new Error("Token must be a Stellar issued token");
        }

        const assetInfo = getStellarTrustLineAsset(token);

        if (!assetInfo) {
          throw new Error("Invalid asset");
        }

        const network = getStellarNetwork(chain.chainId);

        if (network == null) {
          throw new Error(
            `Stellar network not found for chain ${chain.chainId}`
          );
        }

        const client = await getClient<ChainType.STELLAR>(chain);

        const account = await client.getAccount(address);

        const asset = new Asset(assetInfo.code, assetInfo.issuer);

        const changeTrustOperation = Operation.changeTrust({
          asset,
          limit: MAX_ASSET_AMOUNT,
        });

        const builtTransaction = new TransactionBuilder(account, {
          fee: (BigInt(BASE_FEE) * BigInt(2)).toString(),
          networkPassphrase: network,
        })
          .addOperation(changeTrustOperation)
          .setTimeout(300)
          .build();

        const { signedTxXdr } = await stellarSigner.signTransaction(
          builtTransaction.toXDR(),
          {
            networkPassphrase: network,
          }
        );

        const signedTransaction = new Transaction(signedTxXdr, network);

        const sentTransaction = await client.sendTransaction(signedTransaction);

        const txStatus = await client.waitForTransaction(sentTransaction.hash, {
          interval: 1_000,
        });

        if (txStatus !== rpc.Api.GetTransactionStatus.SUCCESS) {
          throw new Error(`Transaction failed with status: ${txStatus}`);
        }

        return true;
      } catch (error) {
        console.error("Error creating trust line:", error);
        return false;
      }
    },
    async onSuccess() {
      queryClient.invalidateQueries({
        queryKey: getPrefixKey(QueryKeys.StellarTrustLine),
      });
    },
  });

  /**
   * Checks if the destination account has created a trust line to receive the given token.
   */
  const isTrustLineApproved = useQuery({
    queryKey: keys().isStellarTrustLineApproved(
      address,
      token?.chainId,
      token?.type,
      token?.address,
      trustLineQuery.data?.limit,
      amount
    ),
    queryFn: async () => {
      if (token?.type !== ChainType.STELLAR) {
        return true;
      }

      // Only stellar issued tokens need a trust line
      // (e.g. not needed for native or contract tokens)
      if (!isStellarIssuedToken(token)) {
        return true;
      }

      if (!amount) {
        throw new Error("Amount is required");
      }

      const limitBn = BigNumber(trustLineQuery.data?.limit || "0");
      const balanceBn = BigNumber(trustLineQuery.data?.balance || "0");

      const availableAllowanceBn = limitBn.minus(balanceBn);

      const amountBn = BigNumber(formatBNToReadable(amount, token.decimals));

      return availableAllowanceBn.gte(amountBn);
    },
    enabled:
      !!address &&
      !!amount &&
      !trustLineQuery?.isLoading &&
      trustLineQuery?.isFetched &&
      token?.type === ChainType.STELLAR,
  });

  return {
    createTrustLine,
    trustLineQuery,
    isTrustLineApproved,
  };
}
